
































import {
  defineComponent, onUnmounted, ref, watch,
} from '@vue/composition-api';
import type { Ref } from '@vue/composition-api';

import { io } from 'socket.io-client';
import Peer from 'simple-peer';

import FanCamVideo from '@/components/FanCam/Video.vue';

const FAN_CAM_WEBRTC_ENDPOINT = process.env.VUE_APP_FANCAM_WEBRTC_ENDPOINT || '';

const Index = defineComponent({
  components: {
    FanCamVideo,
  },
  props: {
    roomID: { type: String },
    grid: { type: Boolean },
    gridColumns: { type: Number, default: 3 },
    freeflow: { type: Boolean },
    freeflowCentering: { type: Boolean },
    freeflowWidth: { type: [Number, String], default: 240 },
    controls: { type: Boolean },
    nameDisplay: { type: Boolean },
  },
  setup(props, { emit }) {
    const isListeningError: Ref<boolean> = ref(false);
    const peers = ref([] as { [key: string]: any }[]);

    const socket: Ref<any> = ref(null);

    function removePeer(connectionRef: string) {
      const index = peers.value.findIndex((p) => p.connectionRef === connectionRef);
      if (index > -1) {
        peers.value.splice(index, 1);
      }
    }

    const streamingConnectionRefs = ref([] as string[]);

    function setStreamTo(connectionRef: string, value: boolean) {
      const index = streamingConnectionRefs.value.indexOf(connectionRef);
      if (value) {
        if (index === -1) streamingConnectionRefs.value.push(connectionRef);
      } else if (!value && index !== -1) {
        streamingConnectionRefs.value.splice(index, 1);
      }
    }

    /**
     * READY = Ready to connect
     * WAITING_SUB_STREAM_SESSION = Joined waiting room
     * TRANSMITTING = LIVE, user's stream is transmitting to staff room
     */
    const stateSubStream = ref<'READY' | 'WAITING_SUB_STREAM_SESSION' | 'TRANSMITTING'>('READY');

    const createRoom = () => {
      if (!socket.value) socket.value = io(FAN_CAM_WEBRTC_ENDPOINT);
      if (!isListeningError.value) {
        socket.value.on('error', ({ message }: any) => {
          alert(message);
        });
        isListeningError.value = true;
      }

      console.log('[Server/User>Tech]: Fan Cam creating room');
      socket.value.emit('init', props.roomID);

      socket.value.on('init-complete', (id: string | number) => {
        console.log('[Server/User>Tech] Room ID: ', id);
        console.warn('[Refactor Diff?] init-complete event yield different id', 'init=', props.roomID, 'event=', id);
        emit('update:roomID', id); // Refactor Notes: used to be roomID = id, but currently roomID SHOULD remains unchanged.
      });

      socket.value.on('peering-start', ({ connectionRef, username, isInitiator }: any) => {
        console.log('[Peer] connecting to', username);
        let peer = new Peer({ initiator: isInitiator }) as any;

        console.log('[Peer] Listening peering data on', `peering-data:${connectionRef}`);
        socket.value.on(`peering-data:${connectionRef}`, ({ payload }: any) => {
          console.log('[Peer] Got signal');
          peer.signal(payload);
        });
        peer.on('signal', (data: any) => {
          socket.value.emit('peering-data', { payload: data, connectionRef });
        });
        peer.on('stream', (stream: any) => {
          console.log('[Peer] Got stream', username);
          peers.value.push({
            connectionRef, peer, username, stream,
          });
        });
        peer.on('connect', () => console.log('[Peer] Connected'));
        peer.on('close', () => {
          console.log('[Peer] Peer closed');
          removePeer(connectionRef);
        });

        // peer.on('close', () => {
        //   console.log('peer closed')
        //   peer.destroy()
        // })

        socket.value.on(`user-left:${connectionRef}`, () => {
          console.log('[Socket] User left', connectionRef);
          peer = undefined;
          socket.value.off(`peering-data:${connectionRef}`);
          socket.value.off(`user-left:${connectionRef}`);
          removePeer(connectionRef);
          setStreamTo(connectionRef, false);
        });
      });
    };

    // Below are all sub stream logics

    const secondaryStreamStore = ref({} as { [key: string]: any });

    function disconnectSubStream(socketConnection: any, peerConnection: any) {
      if (peerConnection) peerConnection.destroy();
      if (socketConnection) {
        socketConnection.emit('user-left');
        socketConnection.disconnect();
      }
    }

    function connectSubStream(roomId: string, subRoomName: string, connectionRef: string) {
      console.log(`[SubConnection to ${subRoomName} / ${connectionRef}] Sending ${connectionRef} to ${roomId}-${subRoomName}`);

      const subSocket = io(FAN_CAM_WEBRTC_ENDPOINT);
      const peerToSend = peers.value.filter((p) => p.connectionRef === connectionRef)[0];

      subSocket.emit('join', { roomID: `${roomId}-${subRoomName}`, username: connectionRef });

      // Waiting room logic
      subSocket.on('waiting-for-room-init', () => {
        subSocket.off('waiting-for-room-init');
        console.log(`[SubConnection to ${subRoomName} / ${connectionRef}] Waiting room...`);
        stateSubStream.value = 'WAITING_SUB_STREAM_SESSION';
        subSocket.on('room-inited', () => {
          subSocket.off();
          secondaryStreamStore.value[connectionRef] = connectSubStream(roomId, subRoomName, connectionRef);
        });
      });

      let subPeer: any;

      // Streaming
      subSocket.on('peering-start', ({ connectionRef: subConnectionRef, isInitiator }: { connectionRef: string, isInitiator: boolean }) => {
        subPeer = new Peer({
          initiator: isInitiator,
          stream: peerToSend.stream,
        });

        subPeer.on('signal', (data: any) => {
          console.log(`[SubPeer to ${subRoomName} / ${connectionRef}] Sending signal`);
          subSocket.emit('peering-data', { payload: data, connectionRef: subConnectionRef });
        });

        console.log(`[SubPeer to ${subRoomName} / ${connectionRef}] listening peering data on`, `peering-data:${subConnectionRef}`);

        subSocket.on(`peering-data:${subConnectionRef}`, ({ payload }: { payload: any }) => {
          console.log(`[SubPeer to ${subRoomName} / ${connectionRef}] Got signal`);
          subPeer.signal(payload);
        });

        subPeer.on('connect', () => {
          console.log(`[SubPeer to ${subRoomName} / ${connectionRef}] Connected`);
          stateSubStream.value = 'TRANSMITTING';
        });

        subSocket.off('peering-start');
        subSocket.on('room-closed', () => {
          stateSubStream.value = 'READY';
          console.log(`[SubPeer to ${subRoomName} / ${connectionRef}] Room closed`);
          disconnectSubStream(subSocket, subPeer);
          // if (subSocket) subSocket.off();
          secondaryStreamStore.value[connectionRef] = connectSubStream(roomId, subRoomName, connectionRef);
        });
      });

      return {
        connectionRef,
        socket: subSocket,
        peer: subPeer,
      };
    }

    watch(streamingConnectionRefs, (newVal) => {
      const streaming = Object.keys(secondaryStreamStore.value);
      const toRemove = streaming.filter((x: string) => !newVal.includes(x));
      const toAdd = newVal.filter((x: string) => !streaming.includes(x));
      toAdd.forEach((s) => {
        secondaryStreamStore.value[s] = connectSubStream(String(props.roomID), 'backend', s);
      });
      toRemove.forEach((s) => {
        disconnectSubStream(secondaryStreamStore.value[s].socket, secondaryStreamStore.value[s].peer);
        delete secondaryStreamStore.value[s];
      });
      emit('metrics:outboundConnections', Object.keys(secondaryStreamStore.value).length);
      if (Object.keys(secondaryStreamStore.value).length === 0) stateSubStream.value = 'READY';
    });

    const reset = () => {
      streamingConnectionRefs.value.splice(0);
      if (socket.value) {
        socket.value.disconnect();
        socket.value = null;
      }
      if (peers.value && peers.value.length > 0) {
        peers.value.forEach(({ peer }) => peer.destroy());
        peers.value.splice(0);
      }
    };

    watch(() => peers.value.length, (newVal) => { emit('metrics:inboundConnections', newVal); });
    watch(stateSubStream, (newVal) => { emit('update:stateSubStream', newVal); });

    watch(() => props.roomID, (newVal) => {
      reset();
      if (newVal) createRoom();
    });

    onUnmounted(() => {
      reset();
    });

    return {
      streamingConnectionRefs,
      isListeningError,
      peers,
      socket,
      createRoom,
      reset,
      setStreamTo,
      stateSubStream,
    };
  },
});

export default Index;
